// About page copy, word for word from the updated "About DIGU" HTML (team list excepted:
// it stays as on the existing About page). Partner logos are not shown until Anthropic
// approves their use (website feedback).
import { ROUTES, CLAUDE_PARTNERS_URL } from './routes';
import { GET_IN_TOUCH } from './getInTouch';
// "Understanding DIGU" image: the legacy understanding-digu-img.jpeg (1 MB) rendered once to webp.
import understandingImage from '@/assets/img/security/understanding-digu.webp';
// Value icons: the legacy About page's own (imported, not changed).
import drivingIcon from '@/assets/img/driving.svg';
import innovationIcon from '@/assets/img/innovation.svg';
import growthIcon from '@/assets/img/growth.svg';
import unlimitedIcon from '@/assets/img/unlimited.svg';
// Team photos are the existing assets used by the legacy About page (imported, not changed).
import sunbid from '@/assets/img/Sunbid.webp';
import sudhir from '@/assets/img/Sudhir.webp';
import avanish from '@/assets/img/Avanish.webp';
import darshan from '@/assets/img/Darshan.webp';
import aman from '@/assets/img/Aman.webp';
import abhishek from '@/assets/img/Abhishek.webp';

export const ABOUT = {
  hero: {
    // Rendered on two lines, as in the HTML.
    titleLines: ['We are engineers', 'and advisors'],
    paragraphs: [
      'DIGU was built by people who spent careers in identity, cloud infrastructure and DevOps. That background is why we do both: we advise on what to secure, then configure the controls ourselves instead of leaving you with a report.',
      'We help organizations adopt Claude and other AI coding agents securely, on their own licenses and in their own environment. Once the controls hold, our engineers stay on to build with you, from DevOps to new products.',
    ],
    nameCard: {
      eyebrow: 'What our name means',
      letters: [
        { letter: 'D', word: 'Driving' },
        { letter: 'I', word: 'Innovation' },
        { letter: 'G', prefix: 'and ', word: 'Growth' },
        { letter: 'U', word: 'Unlimited' },
      ],
      full: 'DIGU Driving Innovation and Growth Unlimited',
    },
  },

  // Copy as in the legacy About page (views/about/Sections/UnderstandingDigu.vue).
  understanding: {
    title: 'Understanding DIGU',
    text: 'DIGU Driving Innovation and Growth Unlimited is more than just our name. It reflects our commitment to helping businesses innovate, grow, and realize what’s possible through technology. DIGU also carries profound significance in Nepali (Newari) culture. The concepts of clan, descent, and generation symbolize who we are, where we come from, and our commitment to supporting Nepal’s future. Rooted in rich heritage, DIGU reflects the flourishing of potential, much like a lotus blooming from humble beginnings to global success.',
    image: understandingImage,
    imageAlt: 'DIGU: Driving Innovation and Growth Unlimited',
  },

  // Shown in the legacy Mission and Vision layout (circle with a ring between the two).
  missionVision: {
    label: 'Mission and vision',
    mission: {
      eyebrow: 'Our mission',
      text: 'We help organizations deploy AI securely, across their teams and products.',
    },
    vision: {
      eyebrow: 'Our vision',
      text: 'To be the leading partner helping organizations learn, deploy, govern and scale AI.',
    },
  },

  // Copy and icons as in the legacy About page "Our Values" (views/about/Sections/GoalSection.vue),
  // shown in the current layout. Each heading is its blue first word plus the rest.
  values: {
    title: 'Our Values',
    lede: 'These values form the foundation of who we are, guiding how we elevate professionals, empower businesses, and fuel innovation without limits.',
    items: [
      {
        accent: 'Driving',
        rest: ' Success',
        text: 'We focus on empowering our customers and our employees to achieve their goals through meaningful collaboration.',
        icon: drivingIcon,
      },
      {
        accent: 'Innovation',
        rest: ' at the Core',
        text: 'Our solutions are designed to spark creativity, enable breakthroughs, and build a better future for businesses and communities.',
        icon: innovationIcon,
      },
      {
        accent: 'Growth',
        rest: ' for All',
        text: 'We enable sustainable growth for our clients while driving digital transformation projects in developing communities',
        icon: growthIcon,
      },
      {
        accent: 'Unlimited',
        rest: ' Possibilities',
        text: 'We are committed to unlocking potential, delivering exceptional results, and creating lasting impact without boundaries.',
        icon: unlimitedIcon,
      },
    ],
  },

  howWeWork: {
    eyebrow: 'How we work with you',
    title: 'Secure first, then build',
    lede: 'Security is where we start, not where we stop. Once the controls hold, the same engineers stay on to put Claude to work in your teams and your products.',
    items: [
      {
        badge: '1',
        title: 'Secure',
        text: 'Assess against the DIGU AI Security Baseline and put the controls in place, in your environment.',
      },
      {
        badge: '2',
        title: 'Deploy',
        text: 'Set up Claude for your teams, from Claude Code in engineering to DevOps and environment provisioning.',
      },
      {
        badge: '3',
        title: 'Build',
        text: 'Build products and features on Claude, with our developers working alongside yours.',
      },
      {
        badge: '4',
        title: 'Scale',
        text: 'Move up the Secure AI Adoption Maturity Roadmap as usage grows, with support when you need it.',
      },
    ],
  },

  howWeAreBuilt: {
    eyebrow: 'How we are built',
    title: 'Certified, senior, and current',
    items: [
      {
        lead: 'Identity heritage.',
        text: ' Our founder and architects came out of the Okta partner ecosystem. Years of enterprise identity work is the reason we start every engagement by asking who the agent is, and what that identity can reach.',
      },
      {
        lead: 'A senior pod on your engagement.',
        text: ' A US based architect leads every engagement, with engineers in the United States, Canada and Nepal. Our Nepal team is a specialist engineering group, not a cost argument, and the time zone gap means work continues while you sleep.',
      },
      {
        lead: 'Certified Claude engineers on every project.',
        text: ' Every engagement is staffed by Claude certified engineers who also carry identity and cloud credentials from years of enterprise work.',
      },
    ],
  },

  // Copy, order, roles and links as in the legacy About page (views/about/Sections/TeamSection.vue).
  team: {
    title: 'Meet The Team',
    linkedinLabel: (name) => `${name} on LinkedIn`,
    members: [
      {
        name: 'Sunbid Shrestha',
        role: 'CEO and Co-Founder',
        photo: sunbid,
        linkedin: 'https://www.linkedin.com/in/sunbidshrestha/',
      },
      {
        name: 'Sudhir Shrestha',
        role: 'COO and Co-Founder',
        photo: sudhir,
        linkedin: 'https://www.linkedin.com/in/sudhir-b-shrestha/',
      },
      // {
      //   name: 'Avanish Shrestha',
      //   role: 'VP of IT and Co-Founder',
      //   photo: avanish,
      //   linkedin: 'https://www.linkedin.com/in/avanishshrestha/',
      // },
      {
        name: 'Darshan Shakya',
        role: 'Co-Founder & AI Advisor',
        photo: darshan,
        linkedin: 'https://www.linkedin.com/in/darshanshakya/',
      },
      {
        name: 'Aman Shakya',
        role: 'Co-Founder & Strategic Advisor',
        photo: aman,
        linkedin: 'https://www.linkedin.com/in/shakyaaman/',
      },
      {
        name: 'Abhishek Shrestha',
        role: 'Country Director - Nepal',
        photo: abhishek,
        linkedin: 'https://www.linkedin.com/in/abhshr89/',
      },
    ],
  },

  partnership: {
    eyebrow: 'Partnerships',
    title: 'Certified in the Claude Partner Network',
    paragraphs: [
      {
        lead: '',
        text: 'The Claude Partner Network is how Anthropic works with the firms that put Claude into production for customers. The Services Track recognizes partners for building a certified delivery team, delivering Claude use cases for clients, and proving the work through public customer references.',
      },
      {
        lead: 'DIGU is Certified in the Claude Partner Network.',
        text: ' On the identity side we are an Okta partner, which matters because agent identity is the control most teams get wrong.',
      },
    ],
    link: {
      label: 'About the program at claude.com/partners',
      href: CLAUDE_PARTNERS_URL,
    },
  },

  whoWeWorkWith: {
    eyebrow: 'Who we work with',
    title: 'Teams that need security to say yes',
    text: 'Organizations where a security team has to sign off before AI can scale. We lead with the industries that need it most.',
    pills: ['Healthcare', 'Financial services', 'Regulated enterprises'],
  },

  scoreCta: {
    title: 'See where you stand in ten questions',
    text: 'The free AI security score is self serve. You get your result on the spot, measured against the same baseline we use.',
    actions: [
      { label: 'Score yourself', to: ROUTES.score, variant: 'bright' },
      {
        label: 'Get the price list',
        to: ROUTES.contact,
        variant: 'ghost-light',
      },
    ],
  },

  // Shared closing band (content/security/getInTouch.js).
  getInTouch: GET_IN_TOUCH,
};
